/**
 * Display helpers for sprint (cycle) windows.
 *
 * Windows are stored half-open — `[plannedStartDate, plannedEndDate)` — so
 * `plannedEndDate` is the exclusive boundary (the next cycle's first day),
 * not a day inside the cycle. Rendering it raw produced "Jul 2 → Jul 9,
 * Day 7/7" on Jul 8, which reads as contradictory. Human-facing surfaces
 * must display the INCLUSIVE last day instead; storage and scheduling math
 * stay half-open.
 */

const MS_PER_DAY = 86_400_000

/**
 * The cycle's last calendar day — `plannedEndDate − 1 day` in UTC
 * `@db.Date` space. The single source of truth for "the end date humans
 * should see" (ranges, "Ended …", "runs until …"). Do not use for the
 * next cycle's start — that IS the exclusive `plannedEndDate`.
 */
export function inclusiveLastDay(plannedEndDate: Date): Date {
	return new Date(plannedEndDate.getTime() - MS_PER_DAY)
}

/**
 * The UTC instant the cycle's window actually lapses: midnight `timeZone`
 * wall-clock on the (exclusive) end date. Mirrors the API's
 * `scheduledCloseInstant(plannedEndDate, 0)`. The raw `plannedEndDate`
 * instant is UTC midnight — 5pm Vancouver the day before — so countdowns
 * against it run ~7-8h early.
 */
export function cycleEndInstant(
	plannedEndDate: Date,
	timeZone = 'America/Vancouver',
): Date {
	const wallClockAsUTC = Date.UTC(
		plannedEndDate.getUTCFullYear(),
		plannedEndDate.getUTCMonth(),
		plannedEndDate.getUTCDate(),
	)
	const guessOffset = zoneOffsetMs(new Date(wallClockAsUTC), timeZone)
	const instant = new Date(wallClockAsUTC - guessOffset)
	const trueOffset = zoneOffsetMs(instant, timeZone)
	if (trueOffset === guessOffset) return instant
	return new Date(wallClockAsUTC - trueOffset)
}

/**
 * Offset (in ms) to add to a UTC instant to get `timeZone` wall-clock time
 * at the instant `at`. DST-aware (derived from Intl, not a constant).
 * Duplicated from the API's private `sprint-dates.ts` helper — the UI can't
 * import from `packages/api`.
 */
function zoneOffsetMs(at: Date, timeZone: string): number {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone,
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
		second: 'numeric',
		hour12: false,
	}).formatToParts(at)
	const get = (type: string) =>
		Number(parts.find((p) => p.type === type)?.value ?? '0')
	// Intl renders midnight as hour 24 in en-US; normalize to 0 so Date.UTC
	// doesn't roll the day forward.
	const hour = get('hour') % 24
	const asUTC = Date.UTC(
		get('year'),
		get('month') - 1,
		get('day'),
		hour,
		get('minute'),
		get('second'),
	)
	return asUTC - at.getTime()
}
